/* eslint-disable @typescript-eslint/no-explicit-any */
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';

import { isEmptyObject } from '@/app/components/search/Utilities.ts';
import { whenMultipleValuesTakeTheFirst } from '@/utils/queryUtils.ts';
import { LeisureType } from '@/domainLogic/index.ts';

const GEODB_LIVE_COUNTRY_SEARCH_COUNTRIES = [
  'AD',
  'AI',
  'BH',
  'BB',
  'CY',
  'CU',
  'DM',
  'FJ',
  'JM',
  'GP',
  'HK',
  'IS',
  'JE',
  'JO',
  'KE',
  'RE',
  'SX',
  'MG',
  'MV',
  'MT',
  'MQ',
  'MU',
  'DO',
  'BL',
  'MF',
  'SN',
  'SC',
  'LK',
  'TN',
  'CR',
  'TZ',
  'QA',
];

export const isValidDate = (d: any) => d instanceof Date && !Number.isNaN(d.valueOf());

export const isString = (val: any): boolean => typeof val === 'string' || val instanceof String;

export const isAnywhere = (val: string | string[] | undefined) => val === 'A';

export const isLeisure = (val: string | string[] | undefined): boolean => {
  switch (val) {
    case LeisureType.CITY_BREAKS:
    case LeisureType.BEACH:
    case LeisureType.SKI:
    case LeisureType.SPA:
    case LeisureType.EXOTIC:
      return true;
    default:
      return false;
  }
};

type IsGeodbLiveCountry = {
  country: string;
  geodbLiveCountrySearch: boolean;
};
const isGeodbLiveCountry = ({ country, geodbLiveCountrySearch }: IsGeodbLiveCountry) =>
  GEODB_LIVE_COUNTRY_SEARCH_COUNTRIES.includes(country.toUpperCase()) && geodbLiveCountrySearch;

export const isCountry = (val: string | string[] | undefined) => val?.length === 2 && !isLeisure(val);

export const isDateParamValue = (dateValue: any): boolean =>
  !!(isString(dateValue) && dateValue.match(/^\d{4}-\d{2}-\d{2}$/) !== null && isValidDate(new Date(dateValue)));

type CreateIsOpenSearchDestinationProps = {
  destination: string | string[] | undefined;
  geodbLiveCountrySearch: boolean;
};
export const createIsOpenSearchDestination = ({
  destination,
  geodbLiveCountrySearch,
}: CreateIsOpenSearchDestinationProps): boolean =>
  isAnywhere(destination) ||
  isLeisure(destination) ||
  (isCountry(destination) &&
    !isGeodbLiveCountry({
      country: whenMultipleValuesTakeTheFirst(destination) || '',
      geodbLiveCountrySearch,
    }));

type IsDestinationsPage = {
  destination?: string | string[];
  dateFrom?: string | string[];
  dateTo?: string | string[];
  geodbLiveCountrySearch: boolean;
};
export const isDestinationsPage = ({
  destination,
  dateFrom,
  dateTo,
  geodbLiveCountrySearch,
}: IsDestinationsPage): boolean => {
  const isOpenSearchDestination = createIsOpenSearchDestination({ destination, geodbLiveCountrySearch });
  const exactDates = isDateParamValue(dateFrom) && isDateParamValue(dateTo);
  return isOpenSearchDestination && exactDates;
};

type IsDestinationsPageFromSearchParams = {
  searchParams: SearchParams;
  geodbLiveCountrySearch: boolean;
};
export const isDestinationsPageFromSearchParams = ({
  searchParams,
  geodbLiveCountrySearch,
}: IsDestinationsPageFromSearchParams): boolean =>
  isEmptyObject(searchParams) ||
  isDestinationsPage({
    destination: searchParams?.[URLParameters.DESTINATION],
    dateFrom: searchParams?.[URLParameters.DATE_FROM],
    dateTo: searchParams?.[URLParameters.DATE_TO],
    geodbLiveCountrySearch,
  });
